/**
 * Shared window palette + chrome tokens.
 *
 * Typography roles:
 * - Born2bSporty (.window-title) — OS window title bar only
 * - Default sans — body copy
 * - font-mono — timestamps, filenames, terminal/meta
 */

const CHROME = {
  light: {
    headerBg: 'bg-[#f5f3f0]',
    headerBar: 'sticky top-0 z-30 border-b border-black/[0.07]',
    pillActive: 'bg-pink-100 border-pink-400/60 text-pink-700',
    pillInactive: 'border-black/[0.07] text-gray-500 hover:border-gray-300 hover:text-gray-700',
    cardNeutral: 'border-black/[0.07] bg-white',
    cardInteractive:
      'border-black/[0.07] bg-white active:bg-black/[0.02] sm:hover:border-black/[0.12]',
    cta: 'bg-pink-500 hover:bg-pink-600 active:bg-pink-700 text-white',
    ctaOutline:
      'border-black/[0.07] text-gray-600 hover:border-gray-400 hover:text-gray-800',
    meta: 'font-mono text-[10px] text-gray-500',
    tag: 'bg-gray-100 text-gray-700 border border-black/[0.06]',
  },
  dark: {
    headerBg: 'bg-[#09070f]',
    headerBar: 'sticky top-0 z-30 border-b border-white/[0.07]',
    pillActive: 'bg-pink-500/20 border-pink-500/60 text-pink-300',
    pillInactive: 'border-white/10 text-white/30 hover:border-white/25 hover:text-white/50',
    cardNeutral: 'border-white/10 bg-white/[0.03]',
    cardInteractive:
      'border-white/10 bg-white/[0.03] active:bg-white/[0.06] sm:hover:border-white/20',
    cta: 'bg-pink-500 hover:bg-pink-600 active:bg-pink-700 text-white',
    ctaOutline:
      'border-white/15 text-white/55 hover:border-white/30 hover:text-white/80',
    meta: 'font-mono text-[10px] text-white/40',
    tag: 'bg-white/[0.06] text-white/60 border border-white/10',
  },
} as const;

export const WINDOW_THEME = {
  light: {
    bg: 'bg-[#f5f3f0]',
    surface: 'bg-white',
    border: 'border-black/[0.07]',
    head: 'text-gray-900',
    body: 'text-gray-600',
    muted: 'text-gray-500',
    dim: 'text-gray-300',
    text: 'text-gray-800',
    divider: 'border-black/[0.07]',
    ...CHROME.light,
  },
  dark: {
    bg: 'bg-[#09070f]',
    surface: 'bg-[#110e1a]',
    border: 'border-white/[0.07]',
    head: 'text-white',
    body: 'text-gray-300',
    muted: 'text-white/40',
    dim: 'text-white/20',
    text: 'text-white',
    divider: 'border-white/[0.07]',
    ...CHROME.dark,
  },
} as const;

export type WindowTheme = typeof WINDOW_THEME.light | typeof WINDOW_THEME.dark;

export function getWindowTheme(isDarkMode = false): WindowTheme {
  return isDarkMode ? WINDOW_THEME.dark : WINDOW_THEME.light;
}
